import React, { useState, useContext } from "react";
import { useToast } from "../utils/useToast";
import GlobalToast from "../components/GlobalToast.jsx";
import { Helmet } from "react-helmet-async";
import Api from "../utils/api";
import contactTranslations from "../locales/contactTranslations";
import { LanguageContext } from "../locales/LanguageContext";

const initialForm = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const Contact = () => {
  const { lang } = useContext(LanguageContext);
  const t = contactTranslations[lang] || {};
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [toast, showToast] = useToast();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await Api.post("contact", form);
      showToast(
        lang === "pa"
          ? "ਤੁਹਾਡਾ ਸੁਨੇਹਾ ਭੇਜ ਦਿੱਤਾ ਗਿਆ ਹੈ। ਧੰਨਵਾਦ!"
          : "Your message has been sent. Thank you!",
        "success",
      );
      setForm(initialForm);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <GlobalToast toast={toast} />
      <Helmet>
        <title>AKAAL KIDS | {t.pageTitle || "Contact"}</title>
      </Helmet>
      <div
        style={{
          width: "100%",
          background: "linear-gradient(90deg, #f6815e 0%, #f6b93b 100%)",
          color: "#fff",
          padding: "38px 0 24px 0",
          textAlign: "center",
          marginBottom: 32,
          boxShadow: "0 4px 24px #f6815e22",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <h1
          style={{
            fontWeight: 900,
            fontSize: "2.6rem",
            letterSpacing: 1,
            marginBottom: 11,
            textShadow: "0 2px 12px #f6815e55",
          }}
        >
          {t.pageTitle}
        </h1>
        <div style={{ fontSize: 20, fontWeight: 500, opacity: 0.96 }}>
          <span role="img" aria-label="contact">
            ✉️
          </span>{" "}
          {t.heroTitle}
        </div>
        <div
          style={{ fontSize: 16, fontWeight: 400, marginTop: 8, opacity: 0.96 }}
        >
          {t.heroDesc}
        </div>
      </div>
      <div className="container py-5" data-aos="fade">
        <div className="row gy-5 gx-lg-5">
          <div className="col-lg-4">
            <div className="info bg-white p-4 rounded shadow-sm h-100">
              <h2
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#111",
                  marginBottom: "0.5rem",
                  letterSpacing: 0,
                }}
              >
                {t.contactInfoTitle}
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    marginTop: 2,
                    color: "#1976d2",
                  }}
                >
                  {t.contactInfo[0]?.icon} {t.contactInfo[0]?.label}
                  <br />
                  <span style={{ fontWeight: 400, color: "#444" }}>
                    {t.contactInfo[0]?.value}
                  </span>
                </div>
              </h2>
              <div style={{ marginTop: 18 }}>
                {t.contactInfo.slice(1).map((info, idx) => (
                  <div
                    key={idx}
                    className="info-item d-flex align-items-start mb-4"
                    style={{
                      borderBottom:
                        idx < t.contactInfo.length - 2
                          ? "1px solid #eee"
                          : "none",
                      paddingBottom: "1.2rem",
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "1.7rem",
                        marginRight: "1rem",
                        marginTop: 2,
                      }}
                    >
                      {info.icon}
                    </span>
                    <div>
                      <h4
                        className="mb-1 d-flex align-items-center"
                        style={{
                          fontSize: "1.08rem",
                          color: "#222",
                          fontWeight: 600,
                        }}
                      >
                        {info.label}:
                      </h4>
                      <p className="mb-0" style={{ color: "#555" }}>
                        {info.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 24 }}>
                <h3
                  style={{
                    fontSize: "1.15rem",
                    color: "#1976d2",
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {t.globalTitle}
                </h3>
                <div
                  style={{
                    color: "#444",
                    fontSize: "1.08rem",
                    marginBottom: 8,
                  }}
                >
                  {t.globalDesc}
                </div>
                <ul
                  style={{
                    color: "#555",
                    fontSize: "1.08rem",
                    marginBottom: 8,
                    paddingLeft: 18,
                  }}
                >
                  {(t.countries || []).map((country, idx) => (
                    <li key={idx}>{country}</li>
                  ))}
                </ul>
                <div
                  style={{
                    color: "#888",
                    fontSize: "1.08rem",
                    marginBottom: 8,
                  }}
                >
                  {t.globalNote}
                </div>
              </div>
              <div style={{ marginTop: 24 }}>
                <h3
                  style={{
                    fontSize: "1.15rem",
                    color: "#1976d2",
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {t.supportTitle}
                </h3>
                <div
                  style={{
                    color: "#444",
                    fontSize: "1.08rem",
                    marginBottom: 8,
                  }}
                >
                  <strong>{t.supportHours}:</strong> <br />
                  {t.supportTime}
                </div>
                <div
                  style={{
                    color: "#888",
                    fontSize: "1.08rem",
                    marginBottom: 8,
                  }}
                >
                  {t.supportNote}
                </div>
              </div>
              <div style={{ marginTop: 24 }}>
                <h3
                  style={{
                    fontSize: "1.15rem",
                    color: "#1976d2",
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {t.guidanceTitle}
                </h3>
                <div
                  style={{
                    color: "#444",
                    fontSize: "1.08rem",
                    marginBottom: 8,
                  }}
                >
                  {t.guidanceDesc}
                </div>
                <ul
                  style={{
                    color: "#555",
                    fontSize: "1.08rem",
                    marginBottom: 8,
                    paddingLeft: 18,
                  }}
                >
                  {(t.guidancePoints || []).map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
                <div
                  style={{
                    color: "#888",
                    fontSize: "1.08rem",
                    marginBottom: 8,
                  }}
                >
                  {t.guidanceNote}
                </div>
              </div>
              <div style={{ marginTop: 24 }}>
                <h3
                  style={{
                    fontSize: "1.15rem",
                    color: "#1976d2",
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {t.parentTitle}
                </h3>
                <div
                  style={{
                    color: "#444",
                    fontSize: "1.08rem",
                    marginBottom: 8,
                  }}
                >
                  {t.parentDesc}
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-8">
            <form
              onSubmit={handleSubmit}
              className="php-email-form bg-white p-4 rounded shadow-sm"
            >
              <div className="row">
                <div className="col-md-6 form-group mb-3">
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    placeholder={lang === "pa" ? "ਤੁਹਾਡਾ ਨਾਮ" : "Your Name"}
                    required
                    value={form.name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 form-group mb-3">
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    placeholder={lang === "pa" ? "ਤੁਹਾਡੀ ਈਮੇਲ" : "Your Email"}
                    required
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  name="subject"
                  placeholder={lang === "pa" ? "ਵਿਸ਼ਾ" : "Subject"}
                  required
                  value={form.subject}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group mb-3">
                <textarea
                  className="form-control"
                  name="message"
                  placeholder={lang === "pa" ? "ਸੁਨੇਹਾ" : "Message"}
                  rows="5"
                  required
                  value={form.message}
                  onChange={handleChange}
                ></textarea>
              </div>
              <div className="my-3">
                {loading && (
                  <div className="loading">
                    {lang === "pa" ? "ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ..." : "Loading..."}
                  </div>
                )}
                {/* Snackbar/Toast messages handled globally */}
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading
                    ? lang === "pa"
                      ? "ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ..."
                      : "Sending..."
                    : lang === "pa"
                      ? "ਸੁਨੇਹਾ ਭੇਜੋ"
                      : "Send Message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
